// React Imports
import React, { useEffect, useState } from "react";

// Internationalization Import
import i18n from "i18next";

// Internal Imports

import { fetchWrapper } from "../../helpers/fetchwrapper";
import { IMediathekDocumentFilterDto } from "../../Models/IMediathekDocumentFilterDto";
import { baseUrl } from "../../helpers/constants";
import { IDokumentgruppe } from "../../Models/IDokumentGruppe";

import queryString from "query-string";

import { IArtikel } from "../../Models/IArtikel";
import { ISprache } from "../../Models/ISprache";
import { ILuebOption } from "../../Models/ILuebOption";
import { StackItemShim, StackShim } from "@fluentui/react-migration-v8-v9";
import {
  Button,
  Dropdown,
  Field,
  Input,
  Option,
} from "@fluentui/react-components";
import { AddRegular, SearchRegular } from "@fluentui/react-icons";

// Props
interface IMediathekDocumentFilterProps {
  isLuebUser: boolean;
  onFilterValuesChanged: any;
  onSearch: any;
  onAddDocumentClicked: any;
  location: any;
}

// Function
function MediathekDocumentFilter(props: IMediathekDocumentFilterProps) {
  const [dokumentgruppen, setDokumentgruppen] = useState<ILuebOption[]>([]);
  const [sprachen, setSprachen] = useState<ILuebOption[]>([]);

  const [allArtikel, setAllArtikel] = useState<IArtikel[]>([]);

  const value = queryString.parse(props?.location?.search);
  const shortId = value.id?.toString();

  const [filterValues, setFilterValues] = useState<IMediathekDocumentFilterDto>(
    {
      textfilter: "",
      shortId: shortId ? shortId : "",
      sprachenGuids: [],
      dokumentGruppenGuids: [],
      artikelId: "",
    }
  );
  const handleFilterValuesChange = (name: string, val: any) => {
    setFilterValues((prevState: any) => ({
      ...prevState,
      [name]: val,
    }));
  };

  const getDokumentGruppen = () => {
    fetchWrapper
      .get(`${baseUrl}/api/mediathek/getDokumentGruppen`)
      .then((data: IDokumentgruppe[]) => {
        let options: ILuebOption[] = [];
        data.forEach((element: IDokumentgruppe) => {
          let option: ILuebOption = {
            key: element.dokumentgruppeGuid,
            text: i18n.language.includes("de")
              ? element.nameDE
              : element.nameEN,
          };
          options.push(option);
        });

        options.sort((a, b) => (a.text < b.text ? -1 : 1));

        setDokumentgruppen(options);
      });
  };

  const getSprachen = () => {
    fetchWrapper
      .get(`${baseUrl}/api/mediathek/getSprachen`)
      .then((data: ISprache[]) => {
        let options: ILuebOption[] = [];
        data.forEach((element: ISprache) => {
          let option: ILuebOption = {
            key: element.spracheGuid,
            text: i18n.language.includes("de")
              ? element.nameDE
              : element.nameEN,
            data: element,
          };
          options.push(option);
        });

        options.sort((a, b) => (a.text < b.text ? -1 : 1));

        setSprachen(options);
      });
  };

  useEffect(() => {
    const defaultLanguages = [];
    const neutral = sprachen.find((x) =>
      x.data.sprachCode?.toLowerCase().includes("non")
    );
    if (neutral) {
      defaultLanguages.push(neutral.key);
    }

    const currentLanguage = sprachen.find((x) =>
      x.data.sprachCode?.toLowerCase().includes(i18n.language)
    );
    if (currentLanguage) {
      defaultLanguages.push(currentLanguage.key);
    }

    handleFilterValuesChange("sprachenGuids", defaultLanguages);
  }, [sprachen]);

  useEffect(() => {
    getDokumentGruppen();
    getSprachen();
  }, []);

  useEffect(() => {
    props.onFilterValuesChanged(filterValues);
  }, [filterValues]);

  return (
    <div>
      <div className="lueb-InnerContentBlockPadding2px">
        <StackShim horizontal tokens={{ childrenGap: 5 }}>
          <Button
            icon={<SearchRegular />}
            appearance="transparent"
            onClick={() => {
              props.onSearch();
            }}
          >
            {i18n.t<string>("search")}
          </Button>
          {props.isLuebUser && (
            <Button
              appearance="transparent"
              icon={<AddRegular />}
              onClick={() => {
                props.onAddDocumentClicked();
              }}
            >
              {i18n.t<string>("addDocument")}
            </Button>
          )}
        </StackShim>
      </div>
      <div className="lueb-ContentBlock">
        <StackShim
          horizontal
          wrap
          tokens={{ childrenGap: 5 }}
          horizontalAlign="space-between"
        >
          <StackItemShim grow={1} style={{ minWidth: 300 }}>
            <Field label={i18n.t<string>("textFilterDescription")}>
              <Input
                value={filterValues?.textfilter}
                onChange={(ev, data) => {
                  handleFilterValuesChange("textfilter", data.value);
                }}
              />
            </Field>
          </StackItemShim>

          <StackItemShim grow={1} style={{ minWidth: 300 }}>
            <Field label={i18n.t<string>("docGroupFilter")}>
              <Dropdown
                multiselect
                selectedOptions={filterValues?.dokumentGruppenGuids}
                onOptionSelect={(event, data) => {
                  if (data.optionValue && filterValues?.dokumentGruppenGuids) {
                    handleFilterValuesChange(
                      "dokumentGruppenGuids",
                      !filterValues.dokumentGruppenGuids.includes(
                        data.optionValue
                      )
                        ? [
                            ...filterValues?.dokumentGruppenGuids,
                            data.optionValue as string,
                          ]
                        : filterValues?.dokumentGruppenGuids.filter(
                            (x) => x != data.optionValue
                          )
                    );
                  }
                }}
              >
                {dokumentgruppen.map((item) => {
                  if (typeof item.key == "string") {
                    return <Option value={item.key}>{item.text}</Option>;
                  }
                })}
              </Dropdown>
            </Field>
          </StackItemShim>

          <StackItemShim grow={1} style={{ minWidth: 300 }}>
            <Field label={i18n.t<string>("languages")}>
              <Dropdown
                multiselect
                selectedOptions={filterValues?.sprachenGuids}
                value={sprachen
                  .filter((x) =>
                    filterValues.sprachenGuids.includes(x.key.toString())
                  )
                  .map((x) => {
                    return x.text;
                  })
                  .join(", ")}
                onOptionSelect={(event, data) => {
                  if (data.optionValue && filterValues?.sprachenGuids) {
                    handleFilterValuesChange(
                      "sprachenGuids",
                      !filterValues.sprachenGuids.includes(data.optionValue)
                        ? [
                            ...filterValues?.sprachenGuids,
                            data.optionValue as string,
                          ]
                        : filterValues?.sprachenGuids.filter(
                            (x) => x != data.optionValue
                          )
                    );
                  }
                }}
              >
                {sprachen.map((item) => {
                  if (typeof item.key == "string") {
                    return <Option value={item.key}>{item.text}</Option>;
                  }
                })}
              </Dropdown>
            </Field>
          </StackItemShim>
        </StackShim>
      </div>
    </div>
  );
}

// Export Function
export default MediathekDocumentFilter;
